// ────────────────────────────────────────────────────────────────────────────
// What each line-table column prints.
//
// Two jobs live here. First, resolving a cell: most columns are a direct read,
// but `date`, `details`, `orderRef` and `cargo` each carry per-column options
// (which date, which end of the journey, which reference slot, product vs
// temperature), and those defaults have to be identical wherever they are read.
//
// Second, the grouping rule. A haulage invoice reads as one row per order with
// its surcharges tucked underneath:
//
//   12/08/26  ORD-10241  Dublin → Ballymount   CUST-88213   780.00
//                        Fuel surcharge 8.5%                 66.30
//                        Waiting time (2.5 hrs)             112.50
//
// So the *context* cells (date, order number, reference, trailer, container,
// cargo) print once per order and stay blank on continuation rows, while
// `description` deliberately does not blank: it carries the route on the
// transport-charge row and the charge text on every other row. That single column is what makes the
// block readable — which is why the old separate `details` column was folded
// into it rather than kept alongside. An org that wants the charge text even
// where a route exists sets `detailsMode: 'description'`.
// ────────────────────────────────────────────────────────────────────────────
import type { DocumentLine } from './documentData';
import { formatMoney, formatQuantity } from './tokens';
import {
  type LineCargoMode,
  type LineColumn,
  type LineColumnKey,
  type LineDateFormat,
  type LineDateSource,
  type LineDetailsMode,
} from './types';

export const DEFAULT_DATE_SOURCE: LineDateSource = 'collection';
export const DEFAULT_DATE_FORMAT: LineDateFormat = 'dd/MM/yyyy';
export const DEFAULT_DETAILS_MODE: LineDetailsMode = 'fromTo';
export const DEFAULT_CARGO_MODE: LineCargoMode = 'both';
/** 1-based; slot 1 is the reference most orgs use for the customer's own ref. */
export const DEFAULT_REFERENCE_SLOT = 1;

/**
 * Columns that describe the *order* rather than the charge. These are the ones
 * blanked on a row that continues the same order.
 */
export const CONTEXT_COLUMNS: readonly LineColumnKey[] = [
  'date',
  'orderNumber',
  'orderRef',
  'trailer',
  'container',
  'cargo',
  // A fuel surcharge under its transport line does not restate the goods.
  'products',
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format an ISO date in the chosen pattern.
 *
 * Reads the date parts off the string rather than going through the local
 * timezone: `new Date('2026-08-12')` is midnight UTC, and formatting that in a
 * behind-UTC zone prints the 11th. Invoice lines are dated, not timestamped.
 */
export function formatLineDate(iso: string | null | undefined, format: LineDateFormat): string {
  if (!iso) return '';
  const [datePart] = iso.split('T');
  const [y, m, d] = (datePart ?? '').split('-').map(Number);
  if (!y || !m || !d) return '';

  const pad = (n: number) => String(n).padStart(2, '0');
  switch (format) {
    case 'dd/MM/yyyy':
      return `${pad(d)}/${pad(m)}/${y}`;
    case 'dd/MM/yy':
      return `${pad(d)}/${pad(m)}/${String(y).slice(-2)}`;
    case 'dd MMM yyyy':
      return `${pad(d)} ${MONTHS[m - 1] ?? ''} ${y}`;
    case 'yyyy-MM-dd':
      return `${y}-${pad(m)}-${pad(d)}`;
  }
}

/** The route text for a line that carries context. */
function detailsText(line: DocumentLine, mode: LineDetailsMode): string {
  const from = line.context?.collectionPlace?.trim();
  const to = line.context?.deliveryPlace?.trim();
  switch (mode) {
    case 'description':
      return line.description;
    case 'collection':
      return from ?? '';
    case 'delivery':
      return to ?? '';
    case 'fromTo':
      // En dash rather than an arrow GLYPH: the standard PDF fonts react-pdf
      // uses without `Font.register` are WinAnsi-encoded, and U+2192 is not in
      // that set — it silently comes out as byte 0x92, a right quote, which is
      // the stray mark seen on the first attempt. The renderer draws a real
      // arrow with vector primitives instead (see `lineRouteEnds`); this string
      // is the fallback for anything measuring the cell as plain text.
      //
      // One end missing is still worth printing — a collection-only job reads
      // better as "Dublin Port" than as "Dublin Port –".
      if (from && to) return `${from} – ${to}`;
      return from ?? to ?? '';
  }
}

function cargoText(line: DocumentLine, mode: LineCargoMode): string {
  const product = line.context?.cargoProduct?.trim();
  const temp = line.context?.cargoTemperatureC;
  const tempText = temp === null || temp === undefined ? '' : `${temp}°C`;
  switch (mode) {
    case 'product':
      return product ?? '';
    case 'temperature':
      return tempText;
    case 'both':
      if (product && tempText) return `${product} · ${tempText}`;
      return product ?? tempText;
  }
}

export interface LineCellOptions {
  currencyIsoCode: string;
  /**
   * True when this row continues an order already printed above it, so the
   * context cells blank out.
   */
  isContinuation: boolean;
}

/**
 * The text for one cell. Returns `''` for a blanked or empty cell — the caller
 * decides whether an empty money column should read as an em dash.
 */
export function lineCellText(
  col: LineColumn,
  line: DocumentLine,
  opts: LineCellOptions,
): string {
  const iso = opts.currencyIsoCode;

  if (opts.isContinuation && CONTEXT_COLUMNS.includes(col.key)) return '';

  switch (col.key) {
    case 'date':
      return formatLineDate(
        (col.dateSource ?? DEFAULT_DATE_SOURCE) === 'delivery'
          ? line.context?.deliveryDate
          : line.context?.collectionDate,
        col.dateFormat ?? DEFAULT_DATE_FORMAT,
      );
    case 'orderNumber':
      return line.orderNumber ?? '';
    case 'description':
      // The route replaces the description ONLY on the transport charge — that
      // is the line the journey describes. Every other charge prints its own
      // text, so a fuel surcharge says "Fuel surcharge" even when it is the
      // only line left on its order.
      return line.context && line.isTransportCharge
        ? detailsText(line, col.detailsMode ?? DEFAULT_DETAILS_MODE) || line.description
        : line.description;
    case 'orderRef': {
      const slot = (col.referenceSlot ?? DEFAULT_REFERENCE_SLOT) - 1;
      return line.context?.references?.[slot]?.trim() ?? '';
    }
    case 'trailer':
      return line.context?.trailer ?? '';
    case 'container':
      return line.context?.container ?? '';
    case 'cargo':
      return cargoText(line, col.cargoMode ?? DEFAULT_CARGO_MODE);
    case 'products':
      return line.products?.trim() ?? '';
    case 'reference':
      return line.reference ?? '';
    case 'quantity':
      return formatQuantity(line.quantity);
    case 'unitPrice':
      return line.unitPrice === null || line.unitPrice === undefined
        ? ''
        : formatMoney(line.unitPrice, iso);
    case 'net':
      return formatMoney(line.net, iso);
    case 'taxRatePct':
      return `${line.taxRatePct}%`;
    case 'taxAmount':
      return formatMoney(line.taxAmount, iso);
    case 'gross':
      return formatMoney(line.gross, iso);
  }
}

/**
 * The two ends of a route, for the cell that can draw an arrow between them.
 *
 * Non-null only when the cell would otherwise have printed "A – B": an arrow is
 * a separator, so there has to be something on both sides of it. Everything
 * else (one-ended routes, non-transport charges, the other detail modes) stays
 * on the plain-text path, which keeps the special case to exactly the rows that
 * need it.
 */
export function lineRouteEnds(
  col: LineColumn,
  line: DocumentLine,
  opts: LineCellOptions,
): { from: string; to: string } | null {
  if (col.key !== 'description') return null;
  if ((col.detailsMode ?? DEFAULT_DETAILS_MODE) !== 'fromTo') return null;
  if (opts.isContinuation && CONTEXT_COLUMNS.includes(col.key)) return null;
  if (!line.context || !line.isTransportCharge) return null;

  const from = line.context.collectionPlace?.trim();
  const to = line.context.deliveryPlace?.trim();
  return from && to ? { from, to } : null;
}

/**
 * Mark each line as first-of-order or a continuation.
 *
 * Grouping is on the order number, and only for *consecutive* lines: invoice
 * line order is meaningful (the operator arranged it), so re-sorting to gather
 * an order's charges would rearrange the document. Lines with no order number
 * are never continuations — an ad-hoc charge has no context to inherit.
 */
export function markContinuations(lines: DocumentLine[]): { line: DocumentLine; isContinuation: boolean }[] {
  let previousOrder: string | null = null;
  return lines.map((line) => {
    const order = line.orderNumber?.trim() || null;
    const isContinuation = order !== null && order === previousOrder;
    previousOrder = order;
    return { line, isContinuation };
  });
}
